// Libs
import React, { useState, useContext, useEffect } from "react";
import { useObserver, KeyValuePair, FieldType, Else } from "@shoothill/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { FormLabel, Box, Typography, Checkbox, FormControlLabel, Radio, FormControl, FormHelperText, Select, InputAdornment, TextField } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models
import { EmploymentStatusEnum } from "Globals/Models/EmploymentStatusEnum";
import { PropertyDetailsModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PropertyDetailsModel";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";

// Views
import { CurrencyInput } from "Globals/Views/CurrencyInput";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";
import { FileUploader } from "Globals/Views/FileUploader";

// Styles
import { theme } from "Globals/Styles/AppTheme";
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { CustomTwoSelects } from "Globals/Styles/CustomTwoSelects";
import { Row } from "Globals/Styles/LayoutStyles";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import CurrentPropertyDetailsViewModel from "Globals/ViewModels/FormSections/CurrentPropertyDetails/CurrentPropertyDetailsViewModel";

// Types
import { UserFile } from "Globals/Models/UserFile";
import { CustomInput } from "Globals/Styles/AppStyling";

export const StyledTextAreaContainer = styled(Box)`
    width: 100%;
`;

interface IProps {
    viewModel: CurrentPropertyDetailsViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const SoldYourHome: React.FC<IProps> = (props: IProps) => {
    const { viewModel, applicationFormViewModel } = props;

    return useObserver(() => (
        <>
            <Row>
                <CustomInput
                    onChange={() => viewModel.handleInputChange()}
                    label="Property in the name of"
                    placeHolder="Enter property in the name of"
                    viewModel={viewModel}
                    fieldName="propertyOwnerName"
                    shrink={true}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    label="Property address line 1"
                    placeHolder="Address line 1"
                    viewModel={viewModel}
                    fieldName="addressLine1"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property address line 2"
                    placeHolder="Address line 2"
                    viewModel={viewModel}
                    fieldName="addressLine2"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property address line 3"
                    placeHolder="Address line 3"
                    viewModel={viewModel}
                    fieldName="addressLine3"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    label="Property city/town"
                    placeHolder="City/Town"
                    viewModel={viewModel}
                    fieldName="city"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property county"
                    placeHolder="County"
                    viewModel={viewModel}
                    fieldName="county"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property postcode"
                    placeHolder="Postcode"
                    viewModel={viewModel}
                    fieldName="postcode"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomDatePicker
                    viewModel={viewModel}
                    label="Date property sale completed?"
                    placeholder={"Enter sale completed date"}
                    fieldName="saleCompletedDate"
                    value={viewModel.model.saleCompletedDate}
                    onChange={(date) => {
                        viewModel.restartTimer();
                        viewModel.setDate("saleCompletedDate", date);
                    }}
                    resetCallback={viewModel.restartTimer}
                    disabled={applicationFormViewModel.model.isSubmitted}
                />
            </Row>
            <Row>
                <CurrencyInput
                    onChange={() => viewModel.handleInputChange()}
                    viewModel={viewModel}
                    label={"Equity received"}
                    smallLabel={"(Less all fees and loans repaid)"}
                    placeholder={"Enter the  equity"}
                    fieldName={"equityReceived"}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row>
            <Row>
                <CustomYesNoRadio
                    label="Are there any arrears or negative equity on the property?"
                    fieldName={"hasNegativeEquity"}
                    value={viewModel.model.hasNegativeEquity}
                    onChange={viewModel.handleRadioChange}
                    disabled={applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("hasNegativeEquity")}
                />
            </Row>
            <Row>
                <FileUploader
                    files={viewModel.getUserFiles}
                    text={"Evidence of sale must be attached"}
                    onUpload={(userFile: UserFile) => {
                        viewModel.model.setValue("arrearsFileName", userFile.fileName);
                        viewModel.model.setValue("arrearsDocumentUrl", userFile.fileUrl);
                        viewModel.handleInputChange();
                    }}
                    onDelete={(userFile: UserFile) => {
                        viewModel.model.setValue("arrearsFileName", "");
                        viewModel.model.setValue("arrearsDocumentUrl", "");
                        viewModel.handleInputChange();
                    }}
                    singleFile={true}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row>
        </>
    ));
};
